<template>
  <layout-editor-page
    :title="formTitle"
    :error="error"
    :isLoading="isLoading"
    :isVisible="isVisible"
    :isBack="isAvailableToSave"
    :isCancel="!isAvailableToSave"
    @command="setCommand"
  >
    <template v-slot:tools>
      <user-actions
        :rfq3ID="rfq3ID"
        @onDisplayCustomer="handlerDisplayCustomer"
        @onPrintExchangeAgreement="handlePrint"
        @onSendExchangeAgreement="handleSendEmail"
      ></user-actions>
    </template>
    <div class="row mt-4">
      <div class="col-12">
        <section-exchange
          :isLoading.sync="isLoading"
          :data="dataRFQ3"
          :model="ModelRFQ3"
          @change="handlerChangeRFQ3"
          @onAutoSave="mixAutoSave"
        ></section-exchange>
      </div>
    </div>
    <dialog-status-customer />
    <dialog-new-instruction-customer />
    <dialog-connect-customer
      ref="dialog-connect-customer"
      filterFieldName="po"
      :filterValueTemplate="filterTemplateConnectCustomer"
    />
    <dialog-send-email-exchange-agreement
      ref="dialog-send-email-exchange-agreement"
      :id="rfq3ID"
      @onSent="handleSentExchange"
    />
  </layout-editor-page>
</template>

<script>
import { routeEditLogic } from '@/mixins/modals'
import { createParams } from '@/utils/dynamic'
import { MainModel } from '@/models/RFQ3'
import { ServiceRFQs } from '@/services/RFQs'
import { ServicePdf } from '@/services/pdf-generators'
import { toDate } from '@/utils/filters'
import { downloadBase64 } from '@/utils/files'
import { AutoSaveMixin } from '@/mixins/FormAutoSave'
import { ServiceRFQ3s } from '@/services/RFQ3s'

export default {
  name: 'EditorExchangeTd',
  mixins: [AutoSaveMixin, routeEditLogic],
  components: {
    'dialog-send-email-exchange-agreement': () => import(/* webpackChunkName: 'RepairExchangeTd' */ './DialogSendExchngeAgreement'),
    'section-exchange': () => import(/* webpackChunkName: 'RepairExchangeTd' */ './SectionExchangeAgreement/Index'),
    'user-actions': () => import(/* webpackChunkName: 'RepairExchangeTd' */ './UserActions'),
    'dialog-connect-customer': () => import(/* webpackChunkName: 'Shared' */ '@/components/Shared/ActivityConnectToCustomerPoFollowUp/Index'),
    'dialog-new-instruction-customer': () => import(/* webpackChunkName: 'DialogNewInstructionCustomer' */ '@/components/Shared/ActivityDialogNewInstructionCustomer'),
    'dialog-status-customer': () => import(/* webpackChunkName: 'ActivityDialogStatusEmailCustomer' */ '@/components/Shared/ActivityDialogStatusEmailCustomer')
  },
  props: {
    config: { type: Object, required: true }
  },
  data () {
    return {
      isNew: true,
      isVisible: false,
      isLoading: false,
      rfq3ID: null,
      error: null,
      dataRFQ3: {}
    }
  },
  computed: {
    formTitle () {
      const { customerCompanyName = '', rfqTypeTitle = '', po = '', partSN, partID } = this.dataRFQ3
      const partNumberData = this.dataSourcePartNumber.map[partID]
      const partNumber = (partNumberData && partNumberData.partNumber) || ''
      return `${customerCompanyName} (${rfqTypeTitle}) - po ${po} for ${partNumber}, part-sn: ${partSN}`
    },
    isAvailableToSave () {
      return !this.isNew
    },
    filterTemplateConnectCustomer () {
      return (filterValue) => {
        return `po == "${filterValue}"`
      }
    },
    dataSourceCurrency () {
      return this.API('Currencies/GetAllDynamic?select=id,title')
    },
    dataSourcePartNumber () {
      let { partID } = this.dataRFQ3
      if (partID) {
        return this.API(`Parts/GetAllDynamic?select=id,partNumber&filter=id==${partID}&take=1`)
      } else {
        return {
          map: {},
          records: []
        }
      }
    }
  },
  methods: {
    ModelRFQ3 () {
      return new MainModel()
    },
    handleSentExchange () {
      this.mixAutoSave({
        field: 'sendMail',
        value: true,
        api: `RFQ3s/Patch/${this.dataRFQ3.id}`
      }).then(() => {
        this.dataRFQ3 = { ...this.dataRFQ3, sendMail: true }
      })

      ServiceRFQs
        .patch({ id: this.dataRFQ3.rfqID, step3: true })
    },
    async handlePrint (logoKey) {
      this.isLoading = true
      const { po } = this.dataRFQ3
      try {
        const { data: pdfBase64 } = await ServicePdf.generateExchangeAgreement({ id: this.dataRFQ3.id, translate: this.L10n, logoKey })
        await downloadBase64(pdfBase64, `ExchAgreement_${po}_${toDate(new Date(), 'YYYY-MM-DD')}`)
      } catch (e) {
        this.requestErrorHandler(e)
      } finally {
        this.isLoading = false
      }
    },
    handleSendEmail (logoType) {
      this.$refs['dialog-send-email-exchange-agreement'].show({ logoType })
    },
    handlerDisplayCustomer () {
      this.$refs['dialog-connect-customer'].show({ po: this.dataRFQ3.po })
    },
    setCommand (command) {
      if (command === 'cancel') {
        this.isVisible = false
        this.mixSetRoute()
      }
      if (command === 'back') {
        this.isVisible = false
        this.mixSetRoute()
        this.$bus.$emit('Refresh-Grid', { id: this.config.id })
      }
    },
    loadData () {
      this.isLoading = true
      return this._loadDataRFQ3()
        .catch((err) => {
          this.error = err
          this.requestErrorHandler(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    _loadDataRFQ3 () {
      let selected = Object.keys(this.ModelRFQ3())
      let params = createParams({
        select: [
          'rfq.po as po',
          'rfq.customerContact.email as customerContactEmail',
          'rfq.customerContact.name as customerContactName',
          'rfq.entryDate as entryDate',
          'rfq.rfqType.title as rfqTypeTitle',
          'rfq.customerCompany.name as customerCompanyName',
          'rfq.id as rfqID',
          'stock.stockTechnicalRemark as stockTechnicalRemark'
        ].concat(selected).join(',')
      })
      return ServiceRFQ3s.getDynamic(this.rfq3ID, params).then(({ data }) => {
        this.dataRFQ3 = data
      })
    },
    handlerChangeRFQ3 (data) {
      this.dataRFQ3 = Object.assign({}, this.dataRFQ3, data)
    },
    // MIXINS
    mixHookState ({ value, state }) {
      if (state === 'edit') {
        this.rfq3ID = value
        this.isNew = false
        this.isVisible = true
        this.loadData()
      }
    },
    mixHookNew () {
      this.isNew = true
      this.isVisible = true
    },
    mixHookReset () {
      this.isNew = true
      this.rfq3ID = null
      this.dataRFQ3 = {}
      this.error = null
    }
  }
}
</script>
